import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Play, Search, Youtube } from 'lucide-react'
import PageHero from '../shared/PageHero'
import { BlogItem, contentApi, YouTubeVideo } from '../../services/contentApi'
import { getImageUrl, IMAGE_CONFIG } from '../../config/settings'

const categoryLabel: Record<string, string> = {
  bhajan: 'Bhajan',
  travel: 'Travel',
  live: 'Live darshan',
  official: 'Official',
  vlog: 'Vlog',
  guide: 'Guide',
  'shyam-jal': 'Shyam Jal',
}

const BlogsPage = () => {
  const [blogs, setBlogs] = useState<BlogItem[]>([])
  const [videos, setVideos] = useState<YouTubeVideo[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  const load = (term?: string) => {
    setLoading(true)
    contentApi.getBlogs(1, term)
      .then((res) => {
        setBlogs(res.data)
        setVideos(res.youtube_videos || [])
      })
      .catch(() => { setBlogs([]); setVideos([]) })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  useEffect(() => {
    document.title = 'Manauna Dham Blogs & Videos | Aonla Stay'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', 'Manauna Dham travel guides, darshan tips, bhajans and YouTube videos — SEO-friendly pilgrim content from Aonla Stay.')
  }, [])

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero
        eyebrow="Manauna Dham · SEO guides"
        title="Stories, guides & videos for your yatra"
        subtitle="Curated articles about Manauna (Manona) Dham plus 30+ YouTube videos — travel, darshan, bhajans, and live streams."
      >
        <form className="max-w-xl flex gap-2" onSubmit={(e) => { e.preventDefault(); load(search.trim() || undefined) }}>
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Manauna topics..." className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <button type="submit" className="px-5 py-3 rounded-xl bg-primary-500 font-semibold hover:bg-primary-600">Search</button>
        </form>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="flex items-center justify-between gap-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><BookOpen className="h-6 w-6 text-primary-500" /> Articles</h2>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">{[1, 2, 3].map((i) => <div key={i} className="h-72 rounded-2xl bg-gray-200 animate-pulse" />)}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
            {blogs.map((blog) => (
              <Link key={blog.id} to={`/blogs/${blog.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-gray-200/80 shadow-sm hover:shadow-xl transition-all">
                <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                  <img src={blog.image ? getImageUrl(blog.image) : IMAGE_CONFIG.FALLBACK_IMAGE} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">{blog.source === 'seo' ? 'Guide' : 'Blog'} · Manauna Dham</p>
                  <h3 className="mt-2 font-bold text-lg text-gray-900 group-hover:text-primary-700 line-clamp-2">{blog.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-3">{blog.summary}</p>
                  {blog.reading_minutes && <p className="mt-3 text-xs text-gray-400">{blog.reading_minutes} min read</p>}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="border-t border-gray-200 pt-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Youtube className="h-6 w-6 text-red-600" /> Manauna Dham videos</h2>
              <p className="mt-2 text-gray-600 max-w-2xl">{videos.length} curated YouTube videos — bhajans, travel guides, official updates, and live darshan.</p>
            </div>
            <a href="https://www.youtube.com/@ManaunadhamBhajan" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary-600 hover:underline">
              View @ManaunadhamBhajan →
            </a>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
            {videos.map((video) => (
              <a
                key={video.video_id}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl overflow-hidden bg-white border border-gray-200 hover:border-red-200 hover:shadow-md transition-all"
              >
                <div className="relative aspect-video bg-gray-900">
                  <img
                    src={`https://i.ytimg.com/vi/${video.video_id}/hqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="h-5 w-5 ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                  <span className="absolute top-2 left-2 rounded-md bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    {categoryLabel[video.category] || video.category}
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-red-700">{video.title}</p>
                  <p className="mt-1 text-xs text-gray-500 truncate">{video.channel}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default BlogsPage
